import {
  Component, OnInit, OnDestroy, inject, Injector, signal, ViewChild, ElementRef,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AgGridModule } from 'ag-grid-angular';

import { ReportsService, AiChatMessage, AiReportResponse } from '../../service/reports.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { SavedReport, ReportEntityType } from '../../shared/models/saved-report.model';
import {
  ENTITY_REGISTRY, EntityRegistryEntry, getEntityRegistryList
} from '../../shared/entity-registry/entity-registry';
import { applyDateRangeFilter, describeDateFilterModel } from '../../shared/components/date-range-filter/date-range-filter.component';
import { SendReportDialogComponent } from '../../shared/components/send-report-dialog/send-report-dialog.component';
import { ReportScheduleDialogComponent } from '../../shared/components/report-schedule-dialog/report-schedule-dialog.component';
import { ReportSchedule } from '../../shared/models/saved-report.model';


@Component({
  selector: 'app-report-builder',
  templateUrl: './report-builder.component.html',
  styleUrls: ['./report-builder.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    AgGridModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatDividerModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule
  ]
})
export class ReportBuilderComponent implements OnInit, OnDestroy {

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private injector = inject(Injector);
  private cdr = inject(ChangeDetectorRef);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private reportsService = inject(ReportsService);
  private sidenavContentService = inject(SidenavContentService);

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;
  @ViewChild('nameInput') nameInput!: ElementRef<HTMLInputElement>;

  // Report name editing
  isEditingName = false;

  // Entity registry
  entityList = getEntityRegistryList();

  // Report state
  reportId = signal<string | null>(null);
  reportName = signal<string>('');
  reportDescription = signal<string>('');
  entityType = signal<ReportEntityType>('contracts');
  isDirty = signal<boolean>(false);
  isLoading = signal<boolean>(true);
  selectedTabIndex = 0; // 0 = AI Assistant, 1 = Manual Builder

  // Filter tracking
  activeFilterCount = signal<number>(0);

  // Schedule
  currentSchedule = signal<ReportSchedule | null>(null);

  // Debug
  showFilterDebug = false;

  // AI conversation
  aiPromptText = '';
  aiMessages: { role: 'user' | 'assistant' | 'system'; content: string; gridState?: any; intent?: string }[] = [];
  aiLoading = signal<boolean>(false);
  aiHasUnappliedReport = false; // true when AI generated a grid state but user hasn't saved yet

  // AG Grid
  gridApi: any = null;
  rowData: any[] = [];
  columnDefs: any[] = [];
  defaultColDef = {
    filter: true,
    floatingFilter: false,
    resizable: true,
    sortable: true,
    enableRowGroup: true,
    enableValue: true,
  };

  sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivots: true,
          suppressPivotMode: true,
        }
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel'
      }
    ],
    defaultToolPanel: 'columns'
  };

  private dataSubscription: Subscription | null = null;
  private currentReport: SavedReport | null = null;

  ngOnInit(): void {
    // Register sidenav toggle for hamburger menu
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) {
        this.actionsSidenav.toggle();
      }
    });

    // Check if we're editing an existing report
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.selectedTabIndex = 1; // Open to Manual Builder when editing
      this.loadReport(id);
    } else {
      // New report — load default entity data, stay on AI tab
      this.loadEntityData(this.entityType());
    }
  }

  ngOnDestroy(): void {
    this.sidenavContentService.unregisterDetailSidenavToggle();
    this.dataSubscription?.unsubscribe();
  }

  /** Called when entity type dropdown changes */
  onEntityTypeChange(newType: ReportEntityType): void {
    this.entityType.set(newType);
    this.isDirty.set(true);
    this.loadEntityData(newType);
  }

  /** Load data from the selected entity's service */
  private loadEntityData(entityType: ReportEntityType): void {
    this.isLoading.set(true);
    this.dataSubscription?.unsubscribe();

    const entry = ENTITY_REGISTRY[entityType];
    if (!entry) {
      console.error(`[ReportBuilder] No registry entry for entity type: ${entityType}`);
      this.isLoading.set(false);
      return;
    }

    // Run setup if needed (e.g., contract columns injector)
    if (entry.setup) {
      entry.setup(this.injector);
    }

    // Set column definitions — show only default columns for fresh reports.
    // applyDateRangeFilter swaps date columns onto the richer date filter
    // (comparison operators + relative presets) and already returns fresh
    // shallow copies, so mutating them here doesn't touch the registry.
    // It's shared with the report preview so both read the same saved models.
    const isLoadingSavedReport = !!this.currentReport;
    this.columnDefs = applyDateRangeFilter(entry.columns).map((col: any) => {
      if (!isLoadingSavedReport) {
        // For new reports, hide columns not in the default set
        const isDefault = entry.defaultColumnIds.includes(col.colId || col.field);
        col.hide = !isDefault;
      }
      return col;
    });

    // Get the service instance and subscribe to its data observable
    const service = this.injector.get(entry.serviceType);
    const observable = service[entry.observableName];

    if (!observable) {
      console.error(`[ReportBuilder] Observable '${entry.observableName}' not found on service for ${entityType}`);
      this.isLoading.set(false);
      return;
    }

    this.dataSubscription = observable.subscribe((data: any[]) => {
      this.rowData = data || [];
      this.isLoading.set(false);
      this.cdr.markForCheck();

      // If loading a saved report, apply grid state after data arrives
      if (this.currentReport && this.gridApi) {
        const report = this.currentReport;
        this.currentReport = null;
        setTimeout(() => this.applyGridState(report), 100);
      }
    });
  }

  /** Load an existing saved report */
  private loadReport(id: string): void {
    this.reportsService.getReport(id).pipe(take(1)).subscribe(report => {
      if (!report) {
        this.snackBar.open('Report not found', 'Dismiss', { duration: 3000 });
        this.router.navigate(['/securehome/reports']);
        return;
      }

      this.reportId.set(report.reportId);
      this.reportName.set(report.name);
      this.reportDescription.set(report.description || '');
      this.entityType.set(report.entityType);
      this.currentSchedule.set(report.schedule || null);
      this.currentReport = report;
      this.isDirty.set(false);

      this.loadEntityData(report.entityType);
    });
  }

  /** Apply saved grid state (column visibility, filters, sorts) */
  private applyGridState(report: SavedReport): void {
    if (!this.gridApi || !report.gridState) return;

    const entry = ENTITY_REGISTRY[report.entityType];
    if (!entry) return;

    // Apply column state — rehydrate function properties from registry columns
    if (report.gridState.columnState?.length > 0) {
      this.gridApi.applyColumnState({
        state: report.gridState.columnState,
        applyOrder: true
      });
    }

    // Apply filter model
    if (report.gridState.filterModel && Object.keys(report.gridState.filterModel).length > 0) {
      this.gridApi.setFilterModel(report.gridState.filterModel);
    }

    this.updateFilterCount();
    this.cdr.markForCheck();
  }

  /** Capture current grid state as a SavedReport-compatible object */
  private captureGridState(): SavedReport['gridState'] {
    if (!this.gridApi) {
      return { columnState: [], filterModel: {}, sortModel: [] };
    }

    return {
      columnState: this.gridApi.getColumnState(),
      filterModel: this.gridApi.getFilterModel(),
      sortModel: [],
      columnLabels: this.captureColumnLabels()
    };
  }

  /**
   * colId -> headerName for every column in the grid.
   *
   * Persisted because `getColumnState()` deliberately omits `headerName` — that's a
   * column *definition* property, not state — so a saved report had no way to say what
   * its own columns are called. The scheduled-report backend was falling back to
   * `humanizeColumnId`, which turns `contractId` into "Contract Id" correctly but has
   * no chance on a colId whose header isn't just its id de-camel-cased.
   *
   * Captured for ALL columns, not just visible ones, so hiding and re-showing a column
   * later doesn't lose its label.
   */
  private captureColumnLabels(): Record<string, string> {
    const labels: Record<string, string> = {};
    this.gridApi.getColumns()?.forEach((col: any) => {
      const colId = col?.getColId?.();
      const headerName = col?.getColDef?.()?.headerName;
      if (colId && headerName) labels[colId] = headerName;
    });
    return labels;
  }

  /** Get visible column IDs from current grid state */
  private getSelectedColumnIds(): string[] {
    if (!this.gridApi) return [];
    return this.gridApi.getColumnState()
      .filter((col: any) => !col.hide)
      .map((col: any) => col.colId);
  }

  // --- Actions ---

  onGridReady(params: any): void {
    this.gridApi = params.api;

    // If we have a pending report to apply, do it now
    if (this.currentReport) {
      const report = this.currentReport;
      this.currentReport = null;
      setTimeout(() => this.applyGridState(report), 100);
    }
  }

  onFilterChanged(): void {
    this.isDirty.set(true);
    this.updateFilterCount();
  }

  onGridStateChanged(): void {
    this.isDirty.set(true);
  }

  /** Count how many column filters are currently active */
  private updateFilterCount(): void {
    if (!this.gridApi) {
      this.activeFilterCount.set(0);
      return;
    }
    const filterModel = this.gridApi.getFilterModel();
    this.activeFilterCount.set(filterModel ? Object.keys(filterModel).length : 0);
  }

  /** Get human-readable description of active filters */
  getFilterDescription(): string {
    if (!this.gridApi) return 'No grid loaded';
    const model = this.gridApi.getFilterModel();
    if (!model || Object.keys(model).length === 0) return 'No filters active';

    const lines: string[] = [];
    for (const [colId, filter] of Object.entries(model) as [string, any][]) {
      const col = this.gridApi.getColumn(colId);
      const name = col?.getColDef()?.headerName || colId;

      if (filter.type === 'dateRangePreset') {
        lines.push(`${name}: ${describeDateFilterModel(filter)}`);
      } else if (filter.type === 'inRange') {
        const from = filter.dateFrom || filter.filter;
        const to = filter.dateTo || filter.filterTo;
        lines.push(`${name}: between ${from} and ${to}`);
      } else if (filter.filterType === 'set' && filter.values) {
        lines.push(`${name}: one of [${filter.values.join(', ')}]`);
      } else if (filter.type && filter.filter !== undefined) {
        lines.push(`${name}: ${filter.type} "${filter.filter}"`);
      } else if (filter.type && filter.dateFrom) {
        lines.push(`${name}: ${filter.type} ${filter.dateFrom}`);
      } else if (filter.condition1 && filter.condition2) {
        const op = filter.operator || 'AND';
        lines.push(`${name}: (${filter.condition1.type} "${filter.condition1.filter || filter.condition1.dateFrom}") ${op} (${filter.condition2.type} "${filter.condition2.filter || filter.condition2.dateFrom}")`);
      } else {
        lines.push(`${name}: ${JSON.stringify(filter)}`);
      }
    }
    return lines.join('\n');
  }

  /** Get raw filter model as formatted JSON */
  getFilterModelJson(): string {
    if (!this.gridApi) return '{}';
    return JSON.stringify(this.gridApi.getFilterModel(), null, 2);
  }

  /** Copy filter debug info to clipboard */
  copyFilterDebug(): void {
    const text = `Filter Description:\n${this.getFilterDescription()}\n\nRaw Filter Model:\n${this.getFilterModelJson()}`;
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('Filter info copied to clipboard', 'Dismiss', { duration: 2000 });
    });
  }

  /** Clear all filters with confirmation */
  clearAllFilters(): void {
    if (!confirm('This will remove all filters from your report. This cannot be undone.\n\nContinue?')) {
      return;
    }

    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.activeFilterCount.set(0);
      this.isDirty.set(true);
      this.cdr.markForCheck();
      this.snackBar.open('All filters cleared', 'Dismiss', { duration: 2000 });
    }
  }

  saveReport(): void {
    const name = this.reportName();
    if (!name.trim()) {
      this.snackBar.open('Please enter a report name', 'Dismiss', { duration: 3000 });
      return;
    }

    const reportData: Partial<SavedReport> = {
      reportId: this.reportId() || undefined,
      name: name.trim(),
      description: this.reportDescription(),
      entityType: this.entityType(),
      gridState: this.captureGridState(),
      selectedColumnIds: this.getSelectedColumnIds(),
      sharedWith: [],
      isPublic: false,
      schedule: this.currentSchedule() || undefined
    };

    this.reportsService.saveReport(reportData).pipe(take(1)).subscribe(saved => {
      this.reportId.set(saved.reportId);
      this.isDirty.set(false);
      this.snackBar.open('Report saved', 'Dismiss', { duration: 2000 });

      // Update URL to include the report ID (so refresh works)
      if (!this.route.snapshot.paramMap.get('id')) {
        this.router.navigate(['/securehome/report-builder', saved.reportId], { replaceUrl: true });
      }

      this.cdr.markForCheck();
    });
  }

  exportCSV(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: `${this.reportName() || 'report'}.csv`
      });
    }
  }

  exportExcel(): void {
    if (this.gridApi) {
      this.gridApi.exportDataAsExcel({
        fileName: `${this.reportName() || 'report'}.xlsx`
      });
    }
  }

  configureSchedule(): void {
    const dialogRef = this.dialog.open(ReportScheduleDialogComponent, {
      data: {
        schedule: this.currentSchedule(),
        reportName: this.reportName() || 'Report'
      }
    });

    dialogRef.afterClosed().subscribe((result: ReportSchedule | null) => {
      if (result === null) return; // cancelled

      this.currentSchedule.set(result);
      this.isDirty.set(true);
      this.cdr.markForCheck();

      // Auto-save if report already has an ID
      if (this.reportId()) {
        this.saveReport();
        this.snackBar.open(
          result.enabled ? 'Schedule saved' : 'Schedule turned off',
          'Dismiss',
          { duration: 3000 }
        );
      } else {
        this.snackBar.open('Save the report to activate the schedule', 'Dismiss', { duration: 3000 });
      }
    });
  }

  sendReportNow(): void {
    this.dialog.open(SendReportDialogComponent, {
      data: {
        reportName: this.reportName() || 'Report',
        gridApi: this.gridApi
      }
    });
  }

  goBackToReports(): void {
    this.router.navigate(['/securehome/reports']);
  }

  onAiSubmit(): void {
    const prompt = this.aiPromptText.trim();
    if (!prompt) return;

    // Add user message to chat
    this.aiMessages.push({ role: 'user', content: prompt });
    this.aiPromptText = '';
    this.aiLoading.set(true);
    this.cdr.markForCheck();

    // Build message history for the backend (only user/assistant, not system)
    const messages: AiChatMessage[] = this.aiMessages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    this.reportsService.generateAiReport(messages, this.entityType()).pipe(take(1)).subscribe({
      next: (response: AiReportResponse) => {
        this.aiLoading.set(false);

        // Handle intent
        if (response.intent === 'ambiguous') {
          this.aiMessages.push({
            role: 'assistant',
            content: response.message || 'Is this a new report or a modification to what we were working on?'
          });
        } else if (response.intent === 'new' && this.aiHasUnappliedReport) {
          // AI thinks this is a new report but we have unsaved work
          this.aiMessages.push({
            role: 'assistant',
            content: response.message,
            gridState: response.gridState,
            intent: 'new'
          });
          this.aiMessages.push({
            role: 'system',
            content: 'This looks like a new report. Would you like to save your current report first, or start fresh?'
          });
        } else {
          // Apply the grid state
          this.aiMessages.push({
            role: 'assistant',
            content: response.message,
            gridState: response.gridState,
            intent: response.intent
          });
          this.applyAiGridState(response.gridState, response.intent);

          // If AI suggested a different entity type, switch
          if (response.suggestedEntityType && response.suggestedEntityType !== this.entityType()) {
            this.aiMessages.push({
              role: 'system',
              content: `Switched data source to ${response.suggestedEntityType}. Not what you wanted? Just let me know which entity type to use.`
            });
            this.onEntityTypeChange(response.suggestedEntityType);
          }
        }

        this.cdr.markForCheck();
      },
      error: (err) => {
        this.aiLoading.set(false);
        this.aiMessages.push({
          role: 'assistant',
          content: 'Sorry, I had trouble generating that report. Please try rephrasing your request.'
        });
        console.error('[ReportBuilder] AI error:', err);
        this.cdr.markForCheck();
      }
    });
  }

  /** Apply AI-generated grid state to the AG Grid */
  private applyAiGridState(gridState: any, intent?: string): void {
    if (!gridState || !this.gridApi) return;

    if (gridState.columnState?.length > 0) {
      this.gridApi.applyColumnState({
        state: gridState.columnState,
        applyOrder: true
      });
    }

    if (gridState.filterModel) {
      if (intent === 'modify') {
        // Merge AI filters with existing filters (don't replace)
        const existing = this.gridApi.getFilterModel() || {};
        const merged = { ...existing, ...gridState.filterModel };
        this.gridApi.setFilterModel(merged);
      } else {
        // New report — replace entirely
        this.gridApi.setFilterModel(gridState.filterModel);
      }
    }

    this.updateFilterCount();
    this.isDirty.set(true);
    this.aiHasUnappliedReport = true;

    // Switch to Manual Builder tab to show results
    this.selectedTabIndex = 1;
    this.cdr.markForCheck();

    this.snackBar.open('Report generated — showing results', 'Dismiss', { duration: 3000 });
  }

  /** Start fresh AI conversation (discard history) */
  startNewAiConversation(): void {
    this.aiMessages = [];
    this.aiHasUnappliedReport = false;
    this.cdr.markForCheck();
  }

  /** Apply a previously generated grid state from a message (when user confirms "start fresh") */
  applyAiMessageGridState(message: any): void {
    if (message.gridState) {
      this.applyAiGridState(message.gridState, message.intent);
    }
  }

  focusNameInput(): void {
    this.isEditingName = true;
    this.cdr.detectChanges();
    // Focus the input after it renders
    setTimeout(() => {
      if (this.nameInput) {
        this.nameInput.nativeElement.focus();
        this.nameInput.nativeElement.select();
      }
    });
  }

  onReportNameChange(name: string): void {
    this.reportName.set(name);
    this.isDirty.set(true);
  }
}
